import React from 'react';
import {
  HStack,
  VStack,
  Text,
  Badge,
  BadgeText,
  Icon,
  Pressable,
  Popover,
  PopoverBackdrop,
  PopoverContent,
  PopoverHeader,
  PopoverBody,
  PopoverCloseButton,
  Heading,
  ScrollView,
} from '@gluestack-ui/themed';
import { AlertCircle } from 'lucide-react-native';
import { useTranslation } from 'react-i18next';
import { formatGapProgress, getGapDetail } from '../utils/gapFormatters';
import type { PlanningGapsWarningProps } from '../types';

const SCROLL_VIEW_STYLE = { paddingHorizontal: 16, paddingTop: 12, paddingBottom: 16 };

/**
 * Component to display planning gaps with detailed segment information.
 */
const PlanningGapsWarning: React.FC<PlanningGapsWarningProps> = ({
  planningGaps,
  totalMissingSlots,
  getEventKindById,
}) => {
  const { t } = useTranslation();
  const [showPopover, setShowPopover] = React.useState(false);

  if (!Array.isArray(planningGaps) || planningGaps.length === 0) {
    return null;
  }

  const validGaps = planningGaps.filter((o) => o && o.moduleId && o.eventKindId && o.severity);

  if (validGaps.length === 0) {
    return null;
  }

  const missingCount = totalMissingSlots || validGaps.reduce((acc, g) => acc + (g.missingCount || 0), 0);

  return (
    <Popover
      isOpen={showPopover}
      onClose={() => setShowPopover(false)}
      onOpen={() => setShowPopover(true)}
      trigger={(triggerProps) => (
        <Pressable {...triggerProps} onPress={() => setShowPopover(true)}>
          <HStack
            space="xs"
            alignItems="center"
            bg="$statusErrorSubtle"
            px="$2"
            py="$1"
            borderRadius="$sm"
            borderColor="$statusError"
            borderWidth={1}
          >
            <Icon as={AlertCircle} size="sm" color="$statusError" />
            <Text size="xs" color="$statusError" fontWeight="medium">
              {t('lecturer.eventCalendar.gaps.unmet', { count: missingCount }) || `${missingCount} Missing Slots`}
            </Text>
          </HStack>
        </Pressable>
      )}
    >
      <PopoverBackdrop />
      <PopoverContent w={400} maxHeight={500}>
        <PopoverHeader borderBottomWidth={1} borderBottomColor="$borderLight">
          <Heading size="sm" color="$textHeading">{t('lecturer.eventCalendar.gaps.title') || 'Planning Gaps'}</Heading>
          <PopoverCloseButton />
        </PopoverHeader>
        <PopoverBody p="$0">
          <ScrollView
            maxHeight={450}
            contentContainerStyle={SCROLL_VIEW_STYLE}
            showsVerticalScrollIndicator
          >
            <VStack space="md">
              <Text size="sm" color="$textBody" mb="$2">
                {t('lecturer.eventCalendar.gaps.unmetMessage')
                  || 'Please schedule time slots for the following mandates:'}
              </Text>
              {validGaps.map((gap, index) => {
                const kindLabel = getEventKindById(gap.eventKindId)?.label || 'Event';
                const progressMsg = formatGapProgress(gap, t);
                const detailMsg = getGapDetail(gap, t);

                return (
                  <HStack
                    key={`${gap.moduleId}-${gap.eventKindId}`}
                    justifyContent="space-between"
                    alignItems="flex-start"
                    borderBottomWidth={index === validGaps.length - 1 ? 0 : 1}
                    borderBottomColor="$borderLight"
                    py="$2"
                  >
                    <VStack flex={1} mr="$2">
                      <Text size="xs" fontWeight="bold" color="$textHeading" numberOfLines={1}>
                        {gap.moduleTitle}
                      </Text>
                      <Text size="xs" color="$textSubtle" numberOfLines={1}>
                        {kindLabel}
                      </Text>
                      {!!detailMsg && (
                        <Text size="xs" color="$statusError" mt="$0.5" fontWeight="medium">
                          {detailMsg}
                        </Text>
                      )}
                    </VStack>
                    <Badge size="sm" action="error" variant="solid" borderRadius="$sm">
                      <BadgeText>{progressMsg}</BadgeText>
                    </Badge>
                  </HStack>
                );
              })}
            </VStack>
          </ScrollView>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};

export default PlanningGapsWarning;
